import type { Constructor, Container, Operation } from '@packages/core/container';

import { StsBackendClient } from '@packages/clients';

/**
 * Base class for domain operations that interact with the STS backend API. Provides constructor
 * injection of {@link StsBackendClient} and automatic IoC container self-registration. Operations
 * that need only the backend client extend this class and implement `exec`; operations with
 * additional dependencies override `registerInContainer` and `constructor`.
 *
 * Replaces the two previous patterns (manual `implements Operation` with boilerplate registration,
 * and `StsBackendProxyOperation` one-liners) with a single, consistent convention.
 *
 * @example
 * ```ts
 * // Simple operation (only needs the backend client):
 * class FetchComments extends StsBackendOperation {
 *     async exec(caseNumber: string): Promise<TechlineCommentEntity[]> {
 *         const response = await this.client.getComments({ caseNumber });
 *         return response?.data?.results?.map(toTechlineCommentEntity) ?? [];
 *     }
 * }
 *
 * // Operation with additional dependencies:
 * class UploadAsset extends StsBackendOperation {
 *     static override registerInContainer(container: Container): void {
 *         container.register(UploadAsset, [StsBackendClient, AnalyticsService]);
 *     }
 *
 *     constructor(client: StsBackendClient, private analyticsService: AnalyticsService) {
 *         super(client);
 *     }
 *
 *     async exec(params: UploadAssetParams): Promise<void> { ... }
 * }
 * ```
 */
export abstract class StsBackendOperation implements Operation {
    /**
     * Register this operation class in the IoC container with {@link StsBackendClient} as its sole
     * dependency. Uses `this` so that subclass constructors register themselves, not the base class.
     * Operations with additional dependencies must override this method.
     *
     * @param container - the container to register in
     */
    static registerInContainer(container: Container): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- static `this` in an abstract class is not assignable to Constructor without a cast; at runtime `this` is always the concrete subclass
        container.register(this as any as Constructor, [StsBackendClient]);
    }

    /** @param client - the STS backend API client */
    constructor(protected client: StsBackendClient) {}

    /**
     * Execute the operation. Subclasses define the specific parameter and return types.
     *
     * @param args - operation-specific arguments
     * @returns operation-specific result
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- variadic exec signature is intentionally untyped at the abstract boundary; subclasses provide precise signatures
    abstract exec(...args: any[]): any;
}
